import { WebsiteConfiguration as WebsiteConfigurationType } from '@/kontent/content-types'
import CustomModal from '@/legacy-ported/components/base/CustomModal'
import { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useState } from 'react'
import { useWebsiteConfiguration } from './contexts/WebsiteConfigurationProvider'

/**
 * Hook to access all website configuration fields + linkedItems.
 * Use this in any component that needs fields from `website_configuration`.
 *
 * Fields available on `elements`:
 *
 * Site config (moved from Homepage):
 *   site_prefix, descriptor, favicon, logo_main
 *
 * Footer (moved from Homepage):
 *   footer_top_content, social_links, footer_menu_links,
 *   acknowledgement, secondary_links, copyright_link
 *
 * Links:
 *   feedback, nesa_main_site
 *
 * Messages:
 *   message_splitview_linkblock, message_download_syllabus_dialog,
 *   message_select_language_dialog, message_prescriptions_collections
 *
 * Tooltips / Syllabus:
 *   glossary_intro, overarchingoutcome_message, nocontent_teachingadvice,
 *   syllabusoutcomes_relatedoutomes, syllabussenioroutcomes_relatedoutomes,
 *   syllabussenioroutcomes_relatedoutomes_ls, syllabusoutcomes_alignedcontent
 *
 * Download config:
 *   max_download_syllabuses, max_download_syllabuses_message
 *
 * KLA/Stage page:
 *   kla_stage_page_left_column_title, kla_stage_page_right_column_title
 */
export const useWebsiteConfigElements = (): {
	elements: WebsiteConfigurationType['elements'] | null
	linkedItems: IContentItemsContainer
} => {
	const wc = useWebsiteConfiguration()
	return {
		elements: wc?.item?.elements ?? null,
		linkedItems: wc?.linkedItems ?? {},
	}
}

/**
 * Header utility-link label that collapses to its first word below the `lg`
 * breakpoint (992px, the NSW DS header breakpoint) so the links don't overlap
 * the search icon / NSW logo on narrow screens (OC2-6163).
 *
 * The whole label stays inside ONE outer span: the parent anchors/buttons are
 * `display: flex; gap: 8px`, so a bare text node + sibling span would become
 * separate flex items and widen the word gap on desktop.
 */
const FirstWordResponsiveLabel = ({ text }: { text: string }) => {
	const spaceIndex = text.indexOf(' ')
	if (spaceIndex === -1) {
		return <span>{text}</span>
	}
	return (
		<span>
			{text.slice(0, spaceIndex)}
			<span className="hidden lg:inline">{text.slice(spaceIndex)}</span>
		</span>
	)
}

/**
 * Renders header utility links ("Feedback" and "NESA website") sourced from
 * the `website_configuration` CMS content item.
 *
 * Feedback link behavior:
 * - `mailto:*` URL: standard anchor that opens the mail client
 * - `http(s)://*` URL (e.g. Microsoft Forms): opens an iframe modal popup
 */
const WebsiteConfiguration = () => {
	const websiteConfiguration = useWebsiteConfiguration()
	const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)

	const feedback = websiteConfiguration?.item?.elements?.feedback?.value || ''
	const nesaUrl =
		websiteConfiguration?.item?.elements?.nesa_main_site?.value || ''

	const isFeedbackMailto = feedback.startsWith('mailto:')
	const isFeedbackFormUrl = /^https?:\/\//.test(feedback)

	if (!feedback && !nesaUrl) return null

	return (
		<div className="nsw-header__utility-links">
			{feedback && isFeedbackMailto && (
				<a href={feedback}>
					<FirstWordResponsiveLabel text="Feedback" />
				</a>
			)}
			{feedback && isFeedbackFormUrl && (
				<button
					type="button"
					onClick={() => setFeedbackModalOpen(true)}
					className="nsw-header__utility-link-button"
				>
					<FirstWordResponsiveLabel text="Feedback" />
				</button>
			)}
			{nesaUrl && (
				<a href={nesaUrl} target="_blank" rel="noopener noreferrer">
					<FirstWordResponsiveLabel text="NESA website" />
				</a>
			)}

			{isFeedbackFormUrl && feedbackModalOpen && (
				<CustomModal
					title="Feedback"
					modalStatus={feedbackModalOpen}
					handleCancel={() => setFeedbackModalOpen(false)}
					hideConfirmButton
					maxWidth="md"
					css={{
						'& .MuiDialog-paper': {
							width: '100%',
							height: '85vh',
						},
						'& .MuiDialogContent-root': {
							overflow: 'hidden',
							padding: 0,
						},
					}}
				>
					<iframe
						src={feedback}
						title="Feedback form"
						style={{
							width: '100%',
							height: '100%',
							border: 0,
						}}
					/>
				</CustomModal>
			)}
		</div>
	)
}

export default WebsiteConfiguration
